<script setup lang="ts">
  import { usePropertyForm } from '../../../model/usePropertyForm';
  import type { RegisterNumberInputProperties as RegisterNumberInputPropsType } from '../../../model/form.types';
  import {
    createCommonInputValidationSchema,
    extractCommonInputValues,
    mapCommonInputValuesToProperties,
    type CommonInputProperties,
  } from '../../../lib/propertyFormHelpers';
  import { FORM_FIELD_CHARACTER_LIMITS } from '../../../lib/form-field-character-limits';
  import {
    FormControl,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    Input,
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
    Switch,
  } from '@/shared/ui';

  interface Props {
    elementId?: string;
  }

  const props = defineProps<Props>();

  usePropertyForm<CommonInputProperties, RegisterNumberInputPropsType>({
    elementId: () => props.elementId,
    validationSchema: createCommonInputValidationSchema(),
    extractInitialValues: extractCommonInputValues,
    mapValuesToProperties: mapCommonInputValuesToProperties,
  });
</script>

<template>
  <div class="flex flex-col">
    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="name"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            ID do campo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="nome_do_campo"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.FIELD_ID"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="placeholder"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Texto auxiliar
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui o texto auxiliar"
              class="h-11"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.AUXILIARY_TEXT"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div class="border-b border-gray-02 py-4 flex flex-col gap-[10px]">
      <FormField
        v-slot="{ componentField }"
        name="label"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Rótulo
          </FormLabel>
          <FormControl>
            <Input
              v-bind="componentField"
              placeholder="Digite aqui"
              class="h-[42px]"
              :maxlength="FORM_FIELD_CHARACTER_LIMITS.LABEL"
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      </FormField>

      <FormField
        v-slot="{ componentField }"
        name="labelPosition"
      >
        <FormItem class="flex flex-col gap-2">
          <FormLabel class="text-sm text-gray-11 font-normal">
            Posicionamento
          </FormLabel>
          <Select v-bind="componentField">
            <FormControl>
              <SelectTrigger class="h-11 w-full">
                <SelectValue placeholder="Selecione a posição" />
              </SelectTrigger>
            </FormControl>
            <SelectContent>
              <SelectItem value="top">
                Acima do campo
              </SelectItem>
              <SelectItem value="left">
                À Esquerda do Campo
              </SelectItem>
            </SelectContent>
          </Select>
          <FormMessage />
        </FormItem>
      </FormField>
    </div>

    <div class="border-b border-gray-02 py-4">
      <FormField
        v-slot="{ componentField }"
        name="required"
      >
        <FormItem class="flex items-center justify-between h-5">
          <FormLabel class="text-sm text-gray-07 font-normal cursor-pointer">
            Obrigatória
          </FormLabel>
          <FormControl>
            <Switch v-bind="componentField" />
          </FormControl>
        </FormItem>
      </FormField>
    </div>
  </div>
</template>
